import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './StoreFrontend.css';

const UiIcon = ({ children }) => <span className="store-ui-icon" aria-hidden="true">{children}</span>;

const StoreFrontend = ({ storeId }) => {
    const [store, setStore] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [products, setProducts] = useState([]);
    const [allProducts, setAllProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [selectedBrand, setSelectedBrand] = useState('all');
    const [showBrandMenu, setShowBrandMenu] = useState(false);
    const [featuredOnly, setFeaturedOnly] = useState(false);
    const [search, setSearch] = useState('');
    const [cartItems, setCartItems] = useState([]);
    const [previewProduct, setPreviewProduct] = useState(null);
    const [previewImage, setPreviewImage] = useState('');
    const [variantProduct, setVariantProduct] = useState(null);
    const [variantSelection, setVariantSelection] = useState({});
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('bank_transfer');
    const [placingOrder, setPlacingOrder] = useState(false);
    const [bankTransferProof, setBankTransferProof] = useState('');
    const [bankTransferProofName, setBankTransferProofName] = useState('');
    const [customer, setCustomer] = useState({
        name: '',
        phone: '',
        email: '',
        address: '',
        city: '',
        postalCode: '',
    });

    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
    const toAmount = (value) => Number(value || 0);
    const customerCacheKey = `public_store_customer_${storeId}`;

    const fetchStoreData = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_BASE_URL}/public/stores/${storeId}`);
            setStore(response.data);
            setError(null);
        } catch (err) {
            console.error('Error fetching store:', err);
            setError('Failed to load store. Please try again later.');
        } finally {
            setLoading(false);
        }
    }, [API_BASE_URL, storeId]);

    const fetchPaymentMethods = useCallback(async () => {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/checkout/stores/${storeId}/payment-methods`
            );
            const methods = response.data.paymentMethods || [];
            setPaymentMethods(methods);
            if (methods.length > 0) setSelectedPaymentMethod(methods[0].type);
        } catch (err) {
            console.error('Error fetching payment methods:', err);
        }
    }, [API_BASE_URL, storeId]);

    const fetchCategories = useCallback(async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/public/stores/${storeId}/categories`);
            const list = Array.isArray(response.data.categories) ? response.data.categories : [];
            setCategories(list.map((item) => (typeof item === 'string' ? { name: item, image_url: '' } : item)));
        } catch (err) {
            console.error('Error fetching categories:', err);
        }
    }, [API_BASE_URL, storeId]);

    const fetchProducts = useCallback(async () => {
        try {
            const params = { page: 1, limit: 100 };
            if (selectedCategory !== 'all') params.category = selectedCategory;
            const response = await axios.get(`${API_BASE_URL}/public/stores/${storeId}/products`, { params });
            const rows = response.data.products || [];
            setAllProducts(rows);
            const filtered = rows.filter((p) => {
                const q = search.trim().toLowerCase();
                const pBrand = String(p.brand || p.metadata?.brand || '').trim();
                const matchesSearch = !q
                    || String(p.name || '').toLowerCase().includes(q)
                    || String(p.sku || '').toLowerCase().includes(q)
                    || pBrand.toLowerCase().includes(q);
                const matchesBrand = selectedBrand === 'all' || pBrand.toLowerCase() === selectedBrand.toLowerCase();
                const matchesFeatured = !featuredOnly || Boolean(p.featured || p.metadata?.featured || p.metadata?.is_featured);
                return matchesSearch && matchesBrand && matchesFeatured;
            });
            setProducts(filtered);
        } catch (err) {
            console.error('Error fetching products:', err);
            setProducts([]);
        }
    }, [API_BASE_URL, featuredOnly, search, selectedBrand, selectedCategory, storeId]);

    useEffect(() => {
        fetchStoreData();
        fetchPaymentMethods();
        fetchCategories();
    }, [fetchCategories, fetchPaymentMethods, fetchStoreData]);

    useEffect(() => {
        try {
            const raw = localStorage.getItem(customerCacheKey);
            if (!raw) return;
            const parsed = JSON.parse(raw);
            setCustomer((prev) => ({ ...prev, ...parsed }));
        } catch {
            // ignore malformed cached data
        }
    }, [customerCacheKey]);

    useEffect(() => {
        try {
            localStorage.setItem(customerCacheKey, JSON.stringify(customer));
        } catch {
            // ignore storage failures
        }
    }, [customer, customerCacheKey]);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    const availableBrands = Array.from(
        new Set(
            allProducts
                .map((p) => String(p.brand || p.metadata?.brand || '').trim())
                .filter(Boolean)
        )
    ).sort((a, b) => a.localeCompare(b));

    // Bank details shown to customers should come from app settings on store payload.
    const selectedBankMethod = (store?.paymentMethods || []).find((m) => m.type === 'bank_transfer') || null;
    const bankName = selectedBankMethod?.bankName || selectedBankMethod?.bank_name || '';
    const accountName = selectedBankMethod?.accountName || selectedBankMethod?.account_name || '';
    const accountNumber = selectedBankMethod?.accountNumber || selectedBankMethod?.account_number || '';
    const bankInstructions = selectedBankMethod?.instructions || selectedBankMethod?.bank_transfer_instructions || '';
    const isBankReceiptRequired = selectedBankMethod?.receiptRequired !== false;

    const getProductVariants = (product) => {
        const variants = product?.metadata?.variants;
        return Array.isArray(variants)
            ? variants.filter((variant) => variant?.name && Array.isArray(variant.values) && variant.values.length > 0)
            : [];
    };

    const getProductImages = (product) => {
        const images = [];
        if (product?.image_url) images.push(product.image_url);
        if (Array.isArray(product?.metadata?.images)) images.push(...product.metadata.images);
        if (product?.metadata?.image_url) images.push(product.metadata.image_url);
        return [...new Set(images.filter(Boolean))];
    };

    const openProductPreview = (product) => {
        const images = getProductImages(product);
        setPreviewProduct(product);
        setPreviewImage(images[0] || '');
    };

    const makeCartKey = (product, selected = {}) => {
        const variantKey = Object.keys(selected)
            .sort()
            .map((key) => `${key}:${selected[key]}`)
            .join('|');
        return variantKey ? `${product.id}__${variantKey}` : String(product.id);
    };

    const addToCart = (product, quantity, selectedVariants = {}) => {
        const cartKey = makeCartKey(product, selectedVariants);
        const existingItem = cartItems.find((item) => item.cartKey === cartKey);

        if (existingItem) {
            setCartItems(
                cartItems.map((item) =>
                    item.cartKey === cartKey
                        ? { ...item, quantity: item.quantity + quantity }
                        : item
                )
            );
        } else {
            setCartItems([...cartItems, { ...product, cartKey, selectedVariants, quantity }]);
        }
    };

    const openVariantSelector = (product) => {
        const initial = {};
        getProductVariants(product).forEach((variant) => {
            initial[variant.name] = variant.values[0] || '';
        });
        setVariantSelection(initial);
        setVariantProduct(product);
    };

    const confirmVariantSelection = () => {
        if (!variantProduct) return;
        const variants = getProductVariants(variantProduct);
        const missing = variants.find((variant) => !variantSelection[variant.name]);
        if (missing) {
            window.alert(`Please select ${missing.name}`);
            return;
        }
        const currentQty = cartItems
            .filter((item) => item.id === variantProduct.id)
            .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
        if (currentQty + 1 > Number(variantProduct.quantity_in_stock || 0)) return;
        addToCart(variantProduct, 1, variantSelection);
        setVariantProduct(null);
        setVariantSelection({});
    };

    const changeItemQty = (product, delta) => {
        if (delta > 0 && getProductVariants(product).length > 0) {
            openVariantSelector(product);
            return;
        }
        const current = cartItems.find((i) => i.id === product.id);
        const nextQty = (current?.quantity || 0) + delta;
        if (nextQty <= 0) {
            setCartItems((prev) => prev.filter((i) => i.cartKey !== current?.cartKey));
            return;
        }
        if (nextQty > Number(product.quantity_in_stock || 0)) return;
        addToCart(product, delta);
    };

    const getItemQty = (productId) => cartItems
        .filter((i) => i.id === productId)
        .reduce((sum, item) => sum + Number(item.quantity || 0), 0);

    const subtotal = cartItems.reduce((sum, item) => sum + toAmount(item.price) * Number(item.quantity || 0), 0);
    const taxRate = toAmount(store?.tax_rate || 0);
    const taxAmount = (subtotal * taxRate) / 100;
    const totalPayable = subtotal + taxAmount;

    const placeOrder = async () => {
        if (cartItems.length === 0) {
            window.alert('Please add at least one product.');
            return;
        }
        if (!customer.name || !customer.phone || !customer.email) {
            window.alert('Customer name, phone and email are required.');
            return;
        }

        if (selectedPaymentMethod === 'bank_transfer' && isBankReceiptRequired && !bankTransferProof) {
            window.alert('Please upload bank transfer receipt before placing order.');
            return;
        }

        setPlacingOrder(true);
        try {
            const payload = {
                customerName: customer.name,
                customerPhone: customer.phone,
                customerEmail: customer.email,
                customerAddress: customer.address,
                customerCity: customer.city,
                customerPostalCode: customer.postalCode,
                items: cartItems.map((item) => ({
                    productId: item.id,
                    quantity: item.quantity,
                    variants: item.selectedVariants || {},
                })),
                paymentMethod: selectedPaymentMethod,
                bankTransferProof: bankTransferProof || null,
                subtotal,
                taxAmount,
                shippingAmount: 0,
                discountAmount: 0,
            };

            const response = await axios.post(`${API_BASE_URL}/public/stores/${storeId}/orders`, payload);
            const createdOrder = response.data || {};

            if (selectedPaymentMethod === 'bank_transfer') {
                window.alert(`Order placed successfully. Order #${createdOrder.orderId}`);
                setCartItems([]);
                setBankTransferProof('');
                setBankTransferProofName('');
                fetchProducts();
                return;
            }

            // Gateway payment flow (card/ussd/mobile money)
            if (createdOrder.gateway === 'paystack') {
                const paystackInit = await axios.post(`${API_BASE_URL}/checkout/public/initialize-paystack`, {
                    storeId,
                    transactionId: createdOrder.transactionId,
                    email: customer.email,
                    amount: createdOrder.totalAmount,
                });
                if (paystackInit.data?.authorizationUrl) {
                    window.location.href = paystackInit.data.authorizationUrl;
                    return;
                }
                throw new Error('Failed to initialize Paystack payment');
            }

            if (createdOrder.gateway === 'flutterwave') {
                const flwInit = await axios.post(`${API_BASE_URL}/checkout/public/initialize-flutterwave`, {
                    storeId,
                    transactionId: createdOrder.transactionId,
                    email: customer.email,
                    amount: createdOrder.totalAmount,
                    phoneNumber: customer.phone,
                    customerName: customer.name,
                    channel: selectedPaymentMethod,
                });
                if (flwInit.data?.paymentLink) {
                    window.location.href = flwInit.data.paymentLink;
                    return;
                }
                throw new Error('Failed to initialize Flutterwave payment');
            }

            throw new Error('No configured payment gateway found for this payment method');
        } catch (err) {
            window.alert(err?.response?.data?.error || 'Failed to place order');
        } finally {
            setPlacingOrder(false);
        }
    };

    if (loading) {
        return (
            <div className="store-frontend loading">
                <div className="loader">Loading store...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="store-frontend error">
                <div className="error-message">
                    <h2>Oops!</h2>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    if (!store) {
        return (
            <div className="store-frontend error">
                <div className="error-message">
                    <h2>Store Not Found</h2>
                    <p>The store you're looking for doesn't exist.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="store-frontend-pos">
            <header className="store-pos-topbar">
                <div className="store-pos-brand">
                    <span className="store-pos-brand-mark">
                        {store.logo_url ? <img src={store.logo_url} alt="" /> : String(store.name || 'R').slice(0, 1)}
                    </span>
                    <span>{store.name}</span>
                </div>
                <div className="store-pos-tools">
                    <input
                        className="store-pos-search"
                        placeholder="Search products, SKU, brand"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <div className="store-pos-pill-wrap">
                        <button
                            type="button"
                            className="store-pos-pill"
                            onClick={() => setShowBrandMenu((prev) => !prev)}
                        >
                            <UiIcon>🏷️</UiIcon>
                            {selectedBrand === 'all' ? 'View All Brands' : selectedBrand}
                        </button>
                        {showBrandMenu ? (
                            <div className="store-brand-menu">
                                <button
                                    type="button"
                                    className={`store-brand-menu-item ${selectedBrand === 'all' ? 'active' : ''}`}
                                    onClick={() => {
                                        setSelectedBrand('all');
                                        setShowBrandMenu(false);
                                    }}
                                >
                                    All Brands
                                </button>
                                {availableBrands.map((brand) => (
                                    <button
                                        key={brand}
                                        type="button"
                                        className={`store-brand-menu-item ${selectedBrand === brand ? 'active' : ''}`}
                                        onClick={() => {
                                            setSelectedBrand(brand);
                                            setShowBrandMenu(false);
                                        }}
                                    >
                                        {brand}
                                    </button>
                                ))}
                            </div>
                        ) : null}
                    </div>
                    <button
                        type="button"
                        className={`store-pos-pill store-pos-pill-accent ${featuredOnly ? 'active' : ''}`}
                        onClick={() => setFeaturedOnly((prev) => !prev)}
                    >
                        <UiIcon>⭐</UiIcon>
                        {featuredOnly ? 'Featured On' : 'Featured'}
                    </button>
                </div>
            </header>

            <main className="store-pos-layout">
                <aside className="store-pos-cats">
                    <button
                        type="button"
                        className={`store-cat-btn ${selectedCategory === 'all' ? 'active' : ''}`}
                        onClick={() => setSelectedCategory('all')}
                    >
                        <span className="store-cat-icon">◌</span>
                        <span>All</span>
                    </button>
                    {categories.map((category) => (
                        <button
                            key={category.name}
                            type="button"
                            className={`store-cat-btn ${selectedCategory === category.name ? 'active' : ''}`}
                            onClick={() => setSelectedCategory(category.name)}
                        >
                            {category.image_url
                                ? <img className="store-cat-image" src={category.image_url} alt={category.name} />
                                : <span className="store-cat-icon">◍</span>}
                            <span>{category.name}</span>
                        </button>
                    ))}
                </aside>

                <section className="store-pos-products">
                    <div className="store-pos-section-head">
                        <div>
                            <h2>Shop Products</h2>
                            <p>{products.length} item{products.length === 1 ? '' : 's'} available</p>
                        </div>
                        <span>{selectedCategory === 'all' ? 'All categories' : selectedCategory}</span>
                    </div>
                    <div className="store-pos-grid">
                        {products.length === 0 ? (
                            <div className="store-pos-empty">No products found in this category.</div>
                        ) : products.map((product) => (
                            <article key={product.id} className="store-pos-card">
                                <button type="button" className="store-pos-card-image" onClick={() => openProductPreview(product)}>
                                    {product.image_url
                                        ? <img src={product.image_url} alt={product.name} />
                                        : <div className="store-pos-image-placeholder">📦</div>}
                                </button>
                                <div className="store-pos-card-meta-row">
                                    <p className="store-pos-card-category">{product.category || 'General'}</p>
                                    <span>{Number(product.quantity_in_stock || 0)} left</span>
                                </div>
                                <h4>{product.name}</h4>
                                {product.description ? <p className="store-pos-card-desc">{product.description}</p> : null}
                                <div className="store-pos-card-bottom">
                                    <strong>₦{toAmount(product.price).toFixed(2)}</strong>
                                    <div className="store-pos-qty-controls">
                                        <button type="button" onClick={() => changeItemQty(product, -1)}>-</button>
                                        <span>{getItemQty(product.id)}</span>
                                        <button type="button" onClick={() => changeItemQty(product, 1)} disabled={!product.available}>+</button>
                                    </div>
                                </div>
                                {getProductVariants(product).length > 0 ? (
                                    <p className="store-pos-variant-note">Options available</p>
                                ) : null}
                            </article>
                        ))}
                    </div>
                </section>

                <aside className="store-pos-order-panel">
                    <div className="store-pos-order-title">
                        <h3>Order List</h3>
                        <span>{cartItems.length}</span>
                    </div>
                    <label className="store-pos-field">
                        <span>Customer Name</span>
                        <input value={customer.name} onChange={(e) => setCustomer((p) => ({ ...p, name: e.target.value }))} />
                    </label>
                    <label className="store-pos-field">
                        <span>Phone</span>
                        <input value={customer.phone} onChange={(e) => setCustomer((p) => ({ ...p, phone: e.target.value }))} />
                    </label>
                    <label className="store-pos-field">
                        <span>Email</span>
                        <input type="email" value={customer.email} onChange={(e) => setCustomer((p) => ({ ...p, email: e.target.value }))} />
                    </label>

                    <div className="store-pos-order-items">
                        {cartItems.length === 0 ? (
                            <p className="store-pos-empty-order">No Products Selected</p>
                        ) : cartItems.map((item) => (
                            <div className="store-pos-order-item" key={item.cartKey || item.id}>
                                <div>
                                    <strong>{item.name}</strong>
                                    {item.selectedVariants && Object.keys(item.selectedVariants).length > 0 ? (
                                        <p className="store-pos-order-variants">
                                            {Object.entries(item.selectedVariants).map(([name, value]) => `${name}: ${value}`).join(' · ')}
                                        </p>
                                    ) : null}
                                    <p>{item.quantity} x ₦{toAmount(item.price).toFixed(2)}</p>
                                </div>
                                <span>₦{(toAmount(item.price) * item.quantity).toFixed(2)}</span>
                            </div>
                        ))}
                    </div>

                    <div className="store-pos-summary">
                        <div><span>Sub Total</span><span>₦{subtotal.toFixed(2)}</span></div>
                        <div><span>Tax ({taxRate.toFixed(2)}%)</span><span>₦{taxAmount.toFixed(2)}</span></div>
                        <div className="store-pos-total"><span>Total Payable</span><span>₦{totalPayable.toFixed(2)}</span></div>
                    </div>

                    <div className="store-pos-payments">
                        <p>Select Payment</p>
                        <div className="store-pos-pay-grid">
                            {paymentMethods.map((method) => (
                                <button
                                    key={method.type}
                                    type="button"
                                    className={`store-pay-chip ${selectedPaymentMethod === method.type ? 'active' : ''}`}
                                    onClick={() => setSelectedPaymentMethod(method.type)}
                                >
                                    {method.name}
                                </button>
                            ))}
                        </div>
                        {paymentMethods.length === 0 ? (
                            <p className="store-pos-gateway-note">No payment methods are configured by admin for this store.</p>
                        ) : null}
                    </div>

                    {selectedPaymentMethod === 'bank_transfer' ? (
                        <>
                            {selectedBankMethod ? (
                                <div className="store-pos-summary store-pos-bank-details">
                                    <div className="store-pos-bank-row"><span>Bank</span><span>{bankName || '-'}</span></div>
                                    <div className="store-pos-bank-row"><span>Account Name</span><span>{accountName || '-'}</span></div>
                                    <div className="store-pos-bank-row"><span>Account Number</span><span>{accountNumber || '-'}</span></div>
                                    {bankInstructions ? <div className="store-pos-bank-row"><span>Instructions</span><span>{bankInstructions}</span></div> : null}
                                </div>
                            ) : (
                                <p className="store-pos-gateway-note">Bank transfer is not configured by admin for this store.</p>
                            )}
                            <label className="store-pos-field">
                                <span>{isBankReceiptRequired ? 'Upload Transfer Receipt (Required)' : 'Upload Transfer Receipt (Optional)'}</span>
                                <label className="store-file-upload">
                                    <span className="store-file-upload-btn">Choose File</span>
                                    <span className="store-file-upload-name">{bankTransferProofName || 'No file chosen'}</span>
                                    <input
                                        type="file"
                                        accept="image/*,.pdf"
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (!file) return;
                                            setBankTransferProofName(file.name || 'Receipt file');
                                            const reader = new FileReader();
                                            reader.onload = () => setBankTransferProof(String(reader.result || ''));
                                            reader.readAsDataURL(file);
                                        }}
                                    />
                                </label>
                            </label>
                        </>
                    ) : null}

                    {selectedPaymentMethod !== 'bank_transfer' ? (
                        <p className="store-pos-gateway-note">After clicking Place Order, you will be redirected to secure gateway checkout.</p>
                    ) : null}

                    <button type="button" className="store-pos-place-btn" onClick={placeOrder} disabled={placingOrder || cartItems.length === 0}>
                        {placingOrder ? 'Placing...' : 'Place Order'}
                    </button>
                </aside>
            </main>

            {previewProduct ? (
                <div className="store-modal-backdrop" role="presentation" onClick={() => setPreviewProduct(null)}>
                    <section className="store-product-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
                        <button type="button" className="store-modal-close" onClick={() => setPreviewProduct(null)}>×</button>
                        <div className="store-product-modal-image">
                            {previewImage
                                ? <img src={previewImage} alt={previewProduct.name} />
                                : <div className="store-pos-image-placeholder">📦</div>}
                            {getProductImages(previewProduct).length > 1 ? (
                                <div className="store-product-thumbs">
                                    {getProductImages(previewProduct).map((image) => (
                                        <button
                                            key={image}
                                            type="button"
                                            className={previewImage === image ? 'active' : ''}
                                            onClick={() => setPreviewImage(image)}
                                        >
                                            <img src={image} alt="" />
                                        </button>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                        <div className="store-product-modal-info">
                            <p>{previewProduct.category || 'General'}</p>
                            <h2>{previewProduct.name}</h2>
                            <strong>₦{toAmount(previewProduct.price).toFixed(2)}</strong>
                            {previewProduct.description ? <span>{previewProduct.description}</span> : null}
                            {getProductVariants(previewProduct).length > 0 ? (
                                <div className="store-product-modal-options">
                                    {getProductVariants(previewProduct).map((variant) => (
                                        <div key={variant.name}>
                                            <b>{variant.name}</b>
                                            <div>{variant.values.join(', ')}</div>
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                            <button
                                type="button"
                                className="store-pos-place-btn"
                                disabled={!previewProduct.available}
                                onClick={() => {
                                    setPreviewProduct(null);
                                    changeItemQty(previewProduct, 1);
                                }}
                            >
                                Add to Order
                            </button>
                        </div>
                    </section>
                </div>
            ) : null}

            {variantProduct ? (
                <div className="store-modal-backdrop" role="presentation" onClick={() => setVariantProduct(null)}>
                    <section className="store-variant-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
                        <button type="button" className="store-modal-close" onClick={() => setVariantProduct(null)}>×</button>
                        <h3>Select Options</h3>
                        <p>{variantProduct.name}</p>
                        {getProductVariants(variantProduct).map((variant) => (
                            <div className="store-variant-group" key={variant.name}>
                                <strong>{variant.name}</strong>
                                <div>
                                    {variant.values.map((value) => (
                                        <button
                                            key={`${variant.name}-${value}`}
                                            type="button"
                                            className={`store-variant-chip ${variantSelection[variant.name] === value ? 'active' : ''}`}
                                            onClick={() => setVariantSelection((prev) => ({ ...prev, [variant.name]: value }))}
                                        >
                                            {value}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                        <button type="button" className="store-pos-place-btn" onClick={confirmVariantSelection}>
                            Add Selected Options
                        </button>
                    </section>
                </div>
            ) : null}

            <footer className="store-pos-footer">
                <p>{store.address} {store.phone ? `• ${store.phone}` : ''}</p>
                <p>Powered by Royaltech POS</p>
            </footer>
        </div>
    );
};

export default StoreFrontend;
